import { ref, computed } from 'vue';
import {
  getAnkorpayBusiness,
  getAnkorpayPaymentMethods,
  getAnkorpayAvailableCredit,
  getAriaDebtorId,
  getAriaKycLink,
  getAriaProviderStatus,
  getAriaMandateData,
  REFUSAL_REASONS_DEBTOR_CLOSED,
  type CreditLimitStatus,
  type AriaProviderStatus,
} from '@monolith/legacy/services/api/ankorpay';
import usePrice from '@core/composables/use-price';
import type { Amount } from '@core/types/amount';
import { captureConvenienceException } from '@client/monolith/core/plugins/sentry/helper';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import { getIbanLast4 } from '@modules/shared/aria/utils/iban.utils';
import { CreditLimitState, ACTIVE_STATUSES, getStateFromStatus } from './credit-limit-state';

// Cache to avoid duplicate concurrent requests
let settingsFetchPromise: Promise<void> | null = null;
let hasFetchedSettings = false;

// Settings-owned singleton refs (Aria data from Ankorpay API)
const isSettingsLoading = ref(false);
const status = ref<CreditLimitStatus>(null);
const ariaAvailableAmount = ref<{ value: number; currency: string }>({ value: 0, currency: 'EUR' });
const debtorId = ref<string | null>(null);
const kycLink = ref<string | null>(null);
const ariaProviderStatus = ref<AriaProviderStatus>(null);
const hasDebtorId = ref(false);
const frozenAt = ref<string | null>(null);
const ibanLast4 = ref('');
const refusalReasons = ref<string[] | null>(null);
const signatureDate = ref('');

// Client-only flag flipped after BI save to force the settings card to render AriaProcessingCard
// while backend catches up. Cleared when a fresh fetch applies a non-refused status.
const isOptimisticallyProcessing = ref(false);

export function clearSettingsState(): void {
  settingsFetchPromise = null;
  hasFetchedSettings = false;
  isSettingsLoading.value = false;
  status.value = null;
  ariaAvailableAmount.value = { value: 0, currency: 'EUR' };
  debtorId.value = null;
  kycLink.value = null;
  ariaProviderStatus.value = null;
  hasDebtorId.value = false;
  frozenAt.value = null;
  refusalReasons.value = null;
  ibanLast4.value = '';
  signatureDate.value = '';
  isOptimisticallyProcessing.value = false;
}

// Module-level helpers — safe to call outside setup / after awaits (do not touch useStore/inject).
export function setSettingsOptimisticProcessing(): void {
  isOptimisticallyProcessing.value = true;
}

export function clearSettingsOptimisticProcessing(): void {
  isOptimisticallyProcessing.value = false;
}

export function getSettingsStatus(): CreditLimitStatus {
  return status.value;
}

/**
 * Fetch Aria status and mandate info for settings page.
 * 3 sequential Ankorpay API calls: business → credit-limits → payment-methods.
 * Module-level so it is safe to call outside setup (e.g. from the poll module after awaits).
 */
export async function fetchAriaCreditLimitImpl(
  paramRetailerUuid: string,
  { force = false }: { force?: boolean } = {}
): Promise<void> {
  if (!paramRetailerUuid) {
    return;
  }

  if (hasFetchedSettings && !force) {
    return settingsFetchPromise ?? undefined;
  }

  isSettingsLoading.value = true;

  const promise = (async () => {
    try {
      const businessResponse = await getAnkorpayBusiness(paramRetailerUuid, ['providers']);
      const fetchedDebtorId = getAriaDebtorId(businessResponse);

      if (!fetchedDebtorId) {
        hasDebtorId.value = false;
        return;
      }

      hasDebtorId.value = true;
      debtorId.value = fetchedDebtorId;
      kycLink.value = getAriaKycLink(businessResponse);
      ariaProviderStatus.value = getAriaProviderStatus(businessResponse);

      const ariaResponse = await getAnkorpayAvailableCredit();
      const ariaAttributes = ariaResponse.data?.data?.attributes;
      status.value = ariaAttributes?.status || null;
      frozenAt.value = ariaAttributes?.frozenAt || null;
      refusalReasons.value = ariaAttributes?.refusalReasons || null;

      if (ariaAttributes?.availableAmount) {
        ariaAvailableAmount.value = {
          value: ariaAttributes.availableAmount.value,
          currency: ariaAttributes.availableAmount.currency || 'EUR',
        };
      }

      if (status.value && (ACTIVE_STATUSES.includes(status.value) || frozenAt.value)) {
        const paymentMethodsResponse = await getAnkorpayPaymentMethods(paramRetailerUuid);
        const mandateData = getAriaMandateData(paymentMethodsResponse);

        ibanLast4.value = getIbanLast4(mandateData?.debtorIban);
        signatureDate.value = mandateData?.signatureDate || '';
      }
    } catch (error) {
      captureConvenienceException(error);
      // On API error, fall back to APPLY state (hasDebtorId=true + status=null) so the user sees
      // the retry CTA rather than an empty section. Contract covered by the "fallback to APPLY"
      // spec in use-credit-limit.spec.ts.
      hasDebtorId.value = true;
      status.value = null;
    } finally {
      isSettingsLoading.value = false;
    }
  })();

  settingsFetchPromise = promise;
  hasFetchedSettings = true;
  return promise;
}

/**
 * Settings page composable — Aria data from Ankorpay API.
 * Fully independent from CMS/pill data.
 * @deprecated Use `usePspAccounts` from `./use-psp-accounts` for new consumers.
 */
export function useCreditLimitSettings() {
  const { formatPrice } = usePrice();

  // Gate Aria-specific UI behind country rollout flag
  const isAriaCountryEnabled = computed(() => isEnabled(FeatureFlag.CreditLimitPillNavbar));

  // Full state machine for settings page (Aria-specific states)
  const state = computed((): CreditLimitState => {
    // Optimistic post-BI-save flag short-circuits to PROCESSING regardless of backend status,
    // until the poll module applies a non-refused fetch result.
    if (isOptimisticallyProcessing.value) {
      return CreditLimitState.PROCESSING;
    }

    if (isSettingsLoading.value) {
      return CreditLimitState.LOADING;
    }

    if (hasDebtorId.value) {
      // REFUSED takes priority — explicit denial overrides frozen state
      if (status.value === 'REFUSED') {
        return CreditLimitState.REFUSED;
      }

      // Frozen check: frozenAt indicates credit is frozen (status may still be REVISED/ACCEPTED)
      if (frozenAt.value) {
        return CreditLimitState.FROZEN;
      }

      if (!status.value) {
        return CreditLimitState.APPLY;
      }

      if (ACTIVE_STATUSES.includes(status.value)) {
        if (ibanLast4.value) {
          return CreditLimitState.ACTIVE_WITH_MANDATE;
        }
        return CreditLimitState.ACTIVE_NO_MANDATE;
      }
    }

    return getStateFromStatus(status.value);
  });

  const isActive = computed(() =>
    [CreditLimitState.ACTIVE, CreditLimitState.ACTIVE_NO_MANDATE, CreditLimitState.ACTIVE_WITH_MANDATE].includes(state.value)
  );
  const isProcessing = computed(() => state.value === CreditLimitState.PROCESSING);
  const isRefused = computed(() => state.value === CreditLimitState.REFUSED);
  const isFrozen = computed(() => state.value === CreditLimitState.FROZEN);
  const isRefusedNotFound = computed(
    () => isRefused.value && (refusalReasons.value?.some((reason) => REFUSAL_REASONS_DEBTOR_CLOSED.includes(reason)) ?? false)
  );

  // Aria section visible when Aria flag enabled and Aria provider exists for this retailer
  const shouldShowAriaCreditLimitSection = computed(() => isAriaCountryEnabled.value && hasDebtorId.value);

  // Aria card actually renders (active with no credit is a hidden gap)
  const hasVisibleAriaCard = computed(
    () => shouldShowAriaCreditLimitSection.value && !(isActive.value && ariaAvailableAmount.value.value <= 0)
  );

  // Aria-specific credit amount (not CMS combined max)
  const formattedAriaAvailableAmount = computed(() =>
    formatPrice(
      {
        amount: Math.max(0, Math.floor(ariaAvailableAmount.value.value)) * 100,
        currency: ariaAvailableAmount.value.currency as Amount['currency'],
      },
      '$0,0'
    )
  );

  /** Force settings state into PROCESSING until a fresh fetch applies a non-refused status. */
  const setOptimisticProcessing = setSettingsOptimisticProcessing;
  const clearOptimisticProcessing = clearSettingsOptimisticProcessing;

  const fetchAriaCreditLimit = fetchAriaCreditLimitImpl;

  return {
    // State
    isLoading: isSettingsLoading,
    status,
    state,
    isActive,
    isProcessing,
    isRefused,
    isFrozen,
    isRefusedNotFound,
    refusalReasons,
    // Aria amounts
    ariaAvailableAmount,
    formattedAriaAvailableAmount,
    // Settings section visibility
    shouldShowAriaCreditLimitSection,
    hasVisibleAriaCard,
    isAriaCountryEnabled,
    // Aria eligibility
    hasDebtorId,
    // Mandate data
    debtorId,
    kycLink,
    ariaProviderStatus,
    ibanLast4,
    signatureDate,
    // Optimistic state
    isOptimisticallyProcessing,
    // Actions
    fetchAriaCreditLimit,
    setOptimisticProcessing,
    clearOptimisticProcessing,
  };
}
